import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Bus, MapPin, Clock, CheckCircle, AlertCircle, XCircle, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentTransportReservation({ user }) {
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [children, setChildren] = useState([]);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [routesRes, subscriptionsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/transport-routes`),
        axios.get(`${API}/transport-subscriptions`),
        axios.get(`${API}/parent/dashboard`)
      ]);

      setRoutes(routesRes.data);
      setSubscriptions(subscriptionsRes.data);
      setChildren(dashboardRes.data.children || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedChild || !selectedRoute) {
      toast.error('Veuillez sélectionner un enfant et une route');
      return;
    }

    try {
      const response = await axios.post(`${API}/transport-subscriptions`, {
        student_id: selectedChild,
        route_id: selectedRoute.id,
        status: 'PENDING',
        start_date: new Date().toISOString(),
        monthly_price: selectedRoute.monthly_price
      });

      toast.success(response.data.message || 'Demande d\'abonnement envoyée avec succès !');
      setSubscriptionDialogOpen(false);
      setSelectedChild('');
      setSelectedRoute(null);
      fetchData();
    } catch (error) {
      console.error('Error creating subscription:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'abonnement';
      
      // Si c'est un tableau d'erreurs Pydantic, extraire le message
      if (Array.isArray(error.response?.data?.detail)) {
        const messages = error.response.data.detail.map(err => err.msg).join(', ');
        toast.error(messages);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Actif</Badge>;
      case 'CANCELLED':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Annulé</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Routes disponibles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bus className="w-5 h-5 mr-2" />
            Routes de transport disponibles
          </CardTitle>
          <CardDescription>
            Abonnez vos enfants au transport scolaire
          </CardDescription>
        </CardHeader>
        <CardContent>
          {routes.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune route disponible pour le moment</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {routes.map((route) => (
                <Card key={route.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <Bus className="w-5 h-5 text-blue-600" />
                      <Badge variant="outline">{route.capacity} places</Badge>
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{route.route_name || route.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{route.description}</p>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span>{route.departure_location} → {route.arrival_location}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>{route.departure_time} - {route.arrival_time}</span>
                      </div>
                      {route.stops_list && route.stops_list.length > 0 && (
                        <div className="text-xs text-gray-500">
                          Arrêts : {route.stops_list.join(', ')}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-green-600">
                        {route.monthly_price?.toLocaleString()} GNF/mois
                      </span>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRoute(route);
                          setSubscriptionDialogOpen(true);
                        }}
                      >
                        S'abonner
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mes abonnements */}
      <Card>
        <CardHeader>
          <CardTitle>Mes abonnements transport</CardTitle>
          <CardDescription>
            Historique de vos abonnements au transport scolaire
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun abonnement pour le moment</p>
          ) : (
            <div className="space-y-3">
              {subscriptions.map((subscription) => (
                <Card key={subscription.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold">{subscription.route_name || 'Route'}</h4>
                          {getStatusBadge(subscription.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Élève :</strong> {subscription.student_name}
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Début :</strong> {new Date(subscription.start_date).toLocaleDateString('fr-FR')}
                        </p>
                        {subscription.end_date && (
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Fin :</strong> {new Date(subscription.end_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        <p className="text-sm font-semibold text-green-600">
                          Prix : {subscription.monthly_price?.toLocaleString()} GNF/mois
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog d'abonnement */}
      <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>S'abonner au transport</DialogTitle>
            <DialogDescription>
              {selectedRoute && (
                <>
                  {selectedRoute.route_name || selectedRoute.name}
                  <br />
                  Prix : {selectedRoute.monthly_price?.toLocaleString()} GNF/mois
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="child">Sélectionner l'enfant</Label>
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger id="child">
                  <SelectValue placeholder="Choisir un enfant" />
                </SelectTrigger>
                <SelectContent>
                  {children.map((child) => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.user_info?.first_name} {child.user_info?.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedRoute && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Détails de la route</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Départ :</strong> {selectedRoute.departure_location} à {selectedRoute.departure_time}</p>
                  <p><strong>Arrivée :</strong> {selectedRoute.arrival_location} à {selectedRoute.arrival_time}</p>
                  {selectedRoute.stops_list && selectedRoute.stops_list.length > 0 && (
                    <p><strong>Arrêts :</strong> {selectedRoute.stops_list.join(', ')}</p>
                  )}
                  <p><strong>Chauffeur :</strong> {selectedRoute.driver_name}</p>
                  <p><strong>Véhicule :</strong> {selectedRoute.vehicle_number}</p>
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                <strong>Note :</strong> Votre demande sera vérifiée par l'administration. 
                Une facture mensuelle vous sera envoyée une fois l'abonnement validé.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSubscriptionDialogOpen(false);
                  setSelectedChild('');
                  setSelectedRoute(null);
                }}
              >
                Annuler
              </Button>
              <Button onClick={handleSubscribe}>
                Confirmer l'abonnement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParentTransportReservation;
